import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/database';

export interface SubscriptionStatus {
  subscribed: boolean;
  tier: string | null;
  subscriptionEnd: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  subscription: SubscriptionStatus;
  refreshSubscription: () => Promise<void>;
  signUp: (email: string, password: string, fullName: string, role?: string, referralCode?: string, marketingConsent?: boolean) => Promise<{ error: Error | null; session: any }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<{ error: Error | null }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultSubscription: SubscriptionStatus = { subscribed: false, tier: null, subscriptionEnd: null };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus>(defaultSubscription);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    
    if (!error && data) {
      setProfile(data as Profile);
    }
  };

  const refreshSubscription = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) {
        console.error('check-subscription error:', error);
        return;
      }
      const newTier = data?.tier ?? null;
      setSubscription({
        subscribed: data?.subscribed ?? false,
        tier: newTier,
        subscriptionEnd: data?.subscription_end ?? null,
      });
      if (data?.subscribed && newTier) {
        setProfile(prev => prev ? { ...prev, subscription_tier: newTier } : null);
      }
    } catch (err) {
      console.error('check-subscription failed:', err);
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer profile fetch to avoid deadlock
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 0);
          setTimeout(() => {
            refreshSubscription();
          }, 0);

          // Detect fresh email verification and send welcome email once
          if (event === 'SIGNED_IN') {
            const confirmedAt = session.user.email_confirmed_at;
            if (confirmedAt) {
              const confirmedTime = new Date(confirmedAt).getTime();
              const now = Date.now();
              const isRecentVerification = now - confirmedTime < 60_000;
              const flagKey = `welcome_email_sent_${session.user.id}`;
              if (isRecentVerification && !localStorage.getItem(flagKey)) {
                localStorage.setItem(flagKey, 'true');
                supabase.functions.invoke('send-welcome-email', {
                  body: { userId: session.user.id },
                }).catch((err) => console.error('Welcome email failed:', err));
              }
            }
          }
        } else {
          setProfile(null);
          setSubscription(defaultSubscription);
        }
        setLoading(false);
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        refreshSubscription();
      }
      setLoading(false);
    });

    return () => authSub.unsubscribe();
  }, [refreshSubscription]);

  const signUp = async (email: string, password: string, fullName: string, role?: string, referralCode?: string, marketingConsent?: boolean) => {
    const redirectUrl = (role === 'instructor' || role === 'both')
      ? `${window.location.origin}/instructor`
      : `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          signup_role: role || 'student',
        },
      },
    });

    // Sync new user to HubSpot (non-blocking)
    if (!error && data.user) {
      try {
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        supabase.functions.invoke('sync-hubspot', {
          body: {
            email,
            firstName,
            lastName,
            properties: { source: 'signup', marketingConsent: marketingConsent ? 'true' : 'false', user_type: 'student' },
          },
        }).catch((err) => console.error('HubSpot sync failed:', err));
      } catch (hubspotErr) {
        console.error('HubSpot sync failed (non-fatal):', hubspotErr);
      }
    }

    // If signup succeeded and there's a referral code, create the referral server-side
    if (!error && data.user && referralCode) {
      try {
        await supabase.functions.invoke('create-referral', {
          body: { referralCode },
        });
      } catch (refError) {
        // Non-fatal: don't block signup if referral fails
        console.error('Referral creation failed:', refError);
      }
    }

    return { error, session: data?.session ?? null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const deleteAccount = async () => {
    try {
      const { error } = await supabase.functions.invoke('delete-account');
      if (error) return { error: new Error(error.message) };
      setUser(null);
      setSession(null);
      setProfile(null);
      return { error: null };
    } catch (err: any) {
      return { error: new Error(err.message || 'Failed to delete account') };
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('Not authenticated') };

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (!error) {
      setProfile(prev => prev ? { ...prev, ...updates } : null);
    }

    return { error };
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      subscription,
      refreshSubscription,
      signUp,
      signIn,
      signOut,
      deleteAccount,
      updateProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
